/**
 * FABRICATED spend-by-expense-category figures for the card Overview tab.
 *
 * NOTHING here is real. There is no per-card spend-by-category endpoint: the
 * only category breakdown the API serves is the org-wide spend dashboard
 * (`GET /reporting/spend-dashboard?chart=spend_by_accounting_category`), which
 * is ERP-derived, has no `creditCardId` dimension, and is gated behind both a
 * Pro tier and a connected ERP integration. Scoping it to one card is a backend
 * change, not a query-param change.
 *
 * Do NOT "make this real" by summing the card's recent transactions into
 * buckets on the client. Transactions carry no GL account, the list is
 * paginated, and a plausible wrong breakdown is worse than an obviously fake
 * one — the same reasoning that keeps `budgetAllocated` fabricated in
 * `cardOverviewStatsDemoData`.
 *
 * Rows are ordered by spend descending so the chart reads as a ranking; the
 * residual `other` bucket is pinned last regardless of its size, matching how
 * the Reporting dashboard folds its long tail.
 *
 * AMOUNTS TOTAL EXACTLY $7,700.00, and that is not arbitrary — it is the
 * `balance` in `cardOverviewStatsDemoData`, which the stats roll-up renders two
 * panels above this chart on the same tab. Fabricated figures still have to
 * agree with each other: a breakdown that sums to something other than the
 * balance shown inches away reads as a bug rather than as sample data. Change
 * one and change the other.
 *
 * Cents are chosen so each bar's compact label matches the design exactly
 * ($3.2K / $1.8K / $1.1K / $980 / $410 / $240) while still summing to $7,700 —
 * which is why travel is 317000 rather than a round 320000.
 *
 * Labels are NOT stored here — `categoryKey` is a stable identifier the panel
 * maps to a translated label. Storing English display text in the fixture would
 * put it beyond the reach of the localization gate.
 */

export type SpendByCategoryKey =
  | 'travel'
  | 'software'
  | 'advertising'
  | 'dining'
  | 'entertainment'
  | 'other'

export type SpendByCategoryDemoRow = {
  categoryKey: SpendByCategoryKey
  /**
   * GL-style expense category code. `null` for the residual bucket, which is an
   * aggregate of several codes rather than one — the panel reads this to keep
   * the tail from ever headlining as the top category.
   */
  categoryCode: string | null
  spendCents: number
}

export const CARD_OVERVIEW_SPEND_BY_CATEGORY_DEMO: SpendByCategoryDemoRow[] = [
  { categoryKey: 'travel', categoryCode: '6100', spendCents: 317000 },
  { categoryKey: 'software', categoryCode: '6300', spendCents: 180000 },
  { categoryKey: 'advertising', categoryCode: '6500', spendCents: 110000 },
  { categoryKey: 'dining', categoryCode: '6200', spendCents: 98000 },
  { categoryKey: 'entertainment', categoryCode: '6400', spendCents: 41000 },
  { categoryKey: 'other', categoryCode: null, spendCents: 24000 },
]
